import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as XIcon } from "@shared/assets/Icons/IconCrossSquare.svg";
import { ReactComponent as IconCheck } from "@shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "@shared/assets/Icons/IconCrossSquare.svg";
import { ReactComponent as IconEdit } from "@shared/assets/Icons/IconEdit_KE.svg";
import GenericInput from "@ui-components/Input/GenericInput";

interface TagProps {
  label: string;
  variant?: "success" | "error" | "warning" | "default" | "neutral" | "custom";
  backgroundColor?: string;
  textColor?: string;
  icon?: React.ReactNode;
  size?: "small" | "medium" | "large";
  bordered?: boolean;
  rounded?: "none" | "small" | "medium" | "full";
  minWidth?: string;
  removable?: boolean;
  onRemove?: () => void;
  /** Optional extra classname(s) */
  className?: string;
  /** Click handler for when the tag itself is clicked */
  onClick?: () => void;
  /** Enable inline editing on hover */
  editable?: boolean;
  /** Current value for editing */
  value?: string;
  /** Callback when save is clicked */
  onSave?: (value: string) => void;
  /** Callback when cancel is clicked */
  onCancel?: () => void;
  /** Show loading state */
  isSaving?: boolean;
  /** Disable editing */
  disabled?: boolean;
  /** Tooltip text shown on hover */
  tooltip?: string;
  /** Truncate long labels with ellipsis (requires a width-constrained parent). */
  truncateLabel?: boolean;
}

const sizeClasses = {
  small: "text-xs px-2 py-1",
  medium: "text-sm px-3 py-1.5",
  large: "text-base px-4 py-2",
};

const roundedClasses = {
  none: "rounded-none",
  small: "rounded-sm",
  medium: "rounded-md",
  full: "rounded-full",
};

const variantClasses = {
  success: "bg-semantic-bg-success-secondary text-semantic-text-success",
  error: "bg-semantic-bg-danger-secondary text-semantic-text-danger",
  warning: "bg-semantic-bg-warning-secondary text-semantic-text-warning",
  default: "bg-gray-100 text-gray-500",
  neutral: "bg-semantic-bg-brand-secondary text-semantic-text-primary",
  custom: "",
};

export const GenericTag: React.FC<TagProps> = ({
  label,
  variant = "default",
  backgroundColor,
  textColor,
  icon,
  size = "small",
  bordered = false,
  rounded = "medium",
  minWidth,
  removable = false,
  onRemove,
  className = "",
  onClick,
  editable = false,
  value,
  onSave,
  onCancel,
  isSaving = false,
  disabled = false,
  tooltip,
  truncateLabel = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? label);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync editValue with value prop when it changes externally
  useEffect(() => {
    if (value !== undefined) {
      setEditValue(value);
    }
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Tooltip hover state and positioning (portal-based to escape overflow:hidden)
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipRect, setTooltipRect] = useState<DOMRect>();
  const tagRef = useRef<HTMLDivElement>(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (tooltipVisible && tagRef.current) {
      setTooltipRect(tagRef.current.getBoundingClientRect());
    }
  }, [tooltipVisible]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  const handleTagEnter = () => {
    if (!tooltip) return;
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setTooltipVisible(true);
  };

  const handleTagLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => setTooltipVisible(false), 100);
  };

  const borderClass = bordered ? "border border-gray-400" : "";

  const variantClass = variantClasses[variant];
  let bgClass = "";
  let customBgStyle = {};
  let finalTextColor = "";

  if (variant === "custom") {
    if (backgroundColor) {
      if (backgroundColor.startsWith("#")) {
        customBgStyle = { backgroundColor };
      } else {
        bgClass = backgroundColor;
      }
    }
    finalTextColor = textColor || "";
  } else {
    bgClass = backgroundColor
      ? backgroundColor.startsWith("#")
        ? ""
        : backgroundColor
      : variantClass.split(" ")[0];
    customBgStyle = backgroundColor?.startsWith("#") ? { backgroundColor } : {};
    finalTextColor = textColor || variantClass.split(" ")[1];
  }

  const inlineStyles = {
    ...customBgStyle,
    ...(minWidth ? { minWidth } : {}),
  };
  const clickableClass = onClick ? "cursor-pointer" : "";

  const handleSave = () => {
    if (onSave && editValue !== undefined) {
      onSave(editValue);
      // Close editing mode - parent component will handle isSaving state
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value ?? label);
    setIsEditing(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  // Get padding and text size classes based on size
  const getInputPadding = () => {
    if (size === "small") return "[&>div>input]:!py-1";
    if (size === "medium") return "[&>div>input]:!py-1.5";
    if (size === "large") return "[&>div>input]:!py-2";
    return "[&>div>input]:!py-1.5";
  };

  const getInputTextSize = () => {
    if (size === "small") return "[&>div>input]:!text-xs";
    if (size === "medium") return "[&>div>input]:!text-sm";
    if (size === "large") return "[&>div>input]:!text-base";
    return "[&>div>input]:!text-sm";
  };

  // Get minimum height based on size to prevent row from growing
  const getMinHeight = () => {
    if (size === "small") return "h-6";
    if (size === "medium") return "h-7";
    if (size === "large") return "h-8";
    return "h-7";
  };

  // Get input height based on size to match tag height
  const getInputHeight = () => {
    if (size === "small") return "[&>div>input]:!h-6";
    if (size === "medium") return "[&>div>input]:!h-7";
    if (size === "large") return "[&>div>input]:!h-8";
    return "[&>div>input]:!h-7";
  };

  // Common input styles for both hover and edit modes to ensure consistency
  const getInputCommonStyles = () => {
    return `!m-0 [&>div]:!m-0 [&>div]:!space-y-0 [&>div>input]:!bg-semantic-bg-primary [&>div>input]:!border [&>div>input]:!border-semantic-brand-primary [&>div>input]:!rounded [&>div>input]:!outline-none [&>div>input]:!text-semantic-text-primary ${getInputPadding()} ${getInputTextSize()} [&>div>input]:!px-2 ${getInputHeight()}`;
  };

  // If saving, show tag format with loader
  if (isSaving && editable) {
    return (
      <div
        className={`inline-flex items-center gap-2 ${
          sizeClasses[size]
        } ${bgClass} ${finalTextColor} ${borderClass} ${
          roundedClasses[rounded]
        } ${getMinHeight()}`}
        style={inlineStyles}
      >
        {icon && <span>{icon}</span>}
        <span
          className="flex items-center justify-center"
          style={{ minWidth: minWidth || "100px" }}
        >
          {value ?? label}
        </span>
        <span className="inline-flex items-center flex-shrink-0">
          <span className="block h-4 w-4 rounded-full border-2 border-gray-300 border-t-gray-500 animate-spin" />
        </span>
        {removable && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 ml-1 rounded hover:bg-gray-200 focus:outline-none"
          >
            <XIcon className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  // If editing, show input with save/cancel buttons
  if (isEditing && editable) {
    return (
      <div
        className={`inline-flex items-center gap-1 ${
          sizeClasses[size]
        } ${bgClass} ${finalTextColor} ${borderClass} ${
          roundedClasses[rounded]
        } ${getMinHeight()}`}
        style={inlineStyles}
      >
        <div
          className="flex-1 min-w-0 flex items-center justify-center"
          style={{ minWidth: "60px", maxWidth: "200px" }}
        >
          <GenericInput
            value={editValue}
            onChange={(val) => setEditValue(val)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            fullWidth={true}
            className={getInputCommonStyles()}
            readOnly={false}
          />
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled}
          className={`inline-flex items-center justify-center ${getMinHeight()} p-0.5 rounded hover:bg-gray-200 focus:outline-none disabled:opacity-50 flex-shrink-0`}
          title="Save"
        >
          <IconCheck className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={disabled}
          className={`inline-flex items-center justify-center ${getMinHeight()} p-0.5 rounded hover:bg-gray-200 focus:outline-none disabled:opacity-50 flex-shrink-0`}
          title="Cancel"
        >
          <IconCancel className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Normal tag display - simple structure when not editing
  const showPointer = editable && !disabled && !isSaving;
  const truncateClass = truncateLabel
    ? "w-fit min-w-0 max-w-full overflow-hidden"
    : "";
  const labelTruncateClass = truncateLabel ? "truncate block min-w-0 max-w-full" : "";

  return (
    <div
      ref={tagRef}
      onMouseEnter={handleTagEnter}
      onMouseLeave={handleTagLeave}
      className={`inline-flex items-center gap-2 max-w-full ${truncateClass} ${sizeClasses[size]} ${bgClass} ${finalTextColor} ${borderClass} ${roundedClasses[rounded]}`}
      style={inlineStyles}
    >
      <div
        onClick={(e) => {
          if (editable && !disabled && !isSaving && !isEditing) {
            e.stopPropagation();
            setIsEditing(true);
          } else if (onClick) {
            onClick();
          }
        }}
        className={`inline-flex items-center gap-2 max-w-full
                      ${truncateClass} ${sizeClasses[size]} ${bgClass} ${finalTextColor}
                      ${borderClass} ${roundedClasses[rounded]}
                      ${
                        showPointer ? "cursor-pointer" : clickableClass
                      } ${className}`}
        style={inlineStyles}
      >
        {icon && <span>{icon}</span>}
        <span className={labelTruncateClass}>{value ?? label}</span>
        {editable && !disabled && !isSaving && !isEditing && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            className="inline-flex items-center justify-center p-0.5 rounded hover:bg-gray-200 focus:outline-none flex-shrink-0"
            title="Edit"
          >
            <IconEdit className="w-3 h-3 text-semantic-text-secondary" />
          </button>
        )}

        {removable && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 ml-1 rounded hover:bg-gray-200 focus:outline-none"
          >
            <XIcon className="w-3 h-3" />
          </button>
        )}
      </div>

      {tooltip && tooltipVisible && tooltipRect && createPortal(
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            top: tooltipRect.bottom + 8,
            left: tooltipRect.left + tooltipRect.width / 2,
            transform: "translateX(-50%)",
          }}
          /* Foreground must come from an inverse token, not a literal white: the
             inverse SURFACE flips to near-white in dark mode, so a hardcoded
             `text-white` rendered the tooltip unreadable there (CTX-243). */
          className="
            bg-semantic-bg-inverse-secondary text-semantic-text-inverse-primary
            text-xs rounded-md px-3 py-2 shadow-lg
            border border-semantic-border-inverse-secondary
            w-max max-w-[320px] text-center
            pointer-events-none
          "
        >
          {tooltip}
        </div>,
        document.body
      )}
    </div>
  );
};
